import * as React from 'react';
import { useBrowser } from '../language/useBrowser';
import { Link } from 'react-router-dom';
import { useRoutes } from '../navigation/routes';
import LocalDiv from '../localization/LocalDiv';
import { FLEX_COLUMN } from '../layout/flexBox';
import AppActions from '../project/sideMenu/AppActions';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import { css } from '@emotion/core';
import { COLORS } from '../globalStyle/colors';
import BulletedMsg from '../layout/BulletedMsg';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import truScoreLogo from '../assets/logo_tbc.svg';
import hrdqLogo from '../assets/logo_hrdq.gif';
import zfLogo from '../assets/logo_zf.svg';
import talentSmartLogo from '../assets/logo_talentsmart.png';
import { useRaterApprovalIds } from '../navigation/hooks';
import { useAppState } from '../core/redux/hooks';


const styles = {
  root:           css`
    padding: 0 10px 15px;
    box-sizing: border-box;

    overflow-y: scroll;
    -ms-overflow-style: none; // IE-specific
    scrollbar-width: none; // Firefox-specific

    ::-webkit-scrollbar {
      width: 0;
      background: transparent;
    }
  `,
  logo:           css`
    width: 184px;
    margin: 20px auto; 
    padding: 0;
  `,
  appActions:     css`
    margin-top: 15px;
    margin-bottom: 15px;
  `,
  footer:         css`
    font-size: 11.5px;
    color: ${COLORS.MEDIUM_GREY};
    
    & > a { 
      text-decoration: none;
      color: ${COLORS.BLUE}
    }
    
    & > a:hover { 
      text-decoration: underline;
    }
    
    @supports (-webkit-touch-callout: none) {
      margin-bottom: 60px;
    }
  `,
  linkContainer:  css`
    border: 1px solid ${COLORS.LIGHTER_GREY};
    border-radius: 8px;
    
    & > * {
      border-bottom: 1px solid ${COLORS.LIGHTER_GREY};
    }
    
    & > *:last-child {
      border-bottom: none;
    }
  `,
  link:           css`
    display: block;
    padding: 18px 15px;
    text-decoration: none;
    
    &:hover {
      cursor: pointer;
    }
  `,
  inactiveLink:   css`
    color: ${COLORS.MEDIUM_GREY};
    
    &:hover {
      background-color: ${COLORS.LIGHTEST_GREY};
    }
  `,
  activeLink:     css`
    color: ${COLORS.DARK_GREY};
    background-color: ${COLORS.LIGHTER_GREY};
    
    &:hover {
      background-color: ${COLORS.LIGHTEST_GREY};
    }
  `,
  iconMediumGrey: css`
    font-size: 15px;
    color: ${COLORS.MEDIUM_GREY};
  `,
  iconGreen:      css`
    font-size: 15px;
    color: ${COLORS.GREEN};
  `,
};


type Props = {
  className?: string;
};


const activeLink = (route: string) => window.location.pathname.endsWith(route);


/**
 * RaterApprovalMenu: a component that renders the contents of the side menu on rater approval pages.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterApprovalMenu: React.FC<Props> = props => {
  const { onIe } = useBrowser();

  const routeIds = useRaterApprovalIds();
  const routes   = useRoutes(routeIds.participantId, routeIds.projDisplayId);

  const config = useAppState(state => state.config);
  const system = (config?.system || '').toLowerCase();
  const projectLogo =  React.useMemo (
    () => system === 'zf'
            ? zfLogo
            : system === 'hrdq'
              ? hrdqLogo
              : system === 'talentsmart'
                ? talentSmartLogo
                : truScoreLogo // always default to TBC
    ,
    [system],
  );   

  return (
    <LocalDiv css={[FLEX_COLUMN, styles.root]} className={props.className}>
      {
        <LocalDiv css={css`height: 48px;`} />
      }

      {
        onIe
          ? (
            <div css={[styles.logo]}>
              <img src={projectLogo} alt='Project Logo' />
            </div>
          )
          : (
            <img src={projectLogo} alt='Project Logo' css={[styles.logo]} />
          )
      }

      {/* Links */}
      <div css={styles.linkContainer}>
        <Link
          to={routes.RaterApprovalDashboard}
          css={[
            styles.link,
            activeLink(routes.RaterApprovalDashboard) ? styles.activeLink : styles.inactiveLink,
          ]}
        >
          <BulletedMsg
            icon={faCircle}
            msg={Str.RaterApproval}
            iconStyle={styles.iconGreen}
            spacePx={14}
          />
        </Link>
      </div>

      {/* Options */}
      <AppActions
        css={styles.appActions}
        loginUrl={routes.RaterApprovalLogin}
        accountUrl={routes.RaterApprovalAccountConfig}
        languageUrl={routes.RaterApprovalLanguage}
        customerServiceUrl={routes.RaterApprovalCustomerService}
      />

      {/* Footer: Copyright/Privacy Policy  */}
      <Msg defaultMsg='' str={Str.FooterCopyrightPrivacy} css={styles.footer} />
    </LocalDiv>
  );
};


export default RaterApprovalMenu;
